import { useMemo, useRef } from 'react';
import { Box, TextField } from '@mui/material';

const VerificationCodeInput = ({
    value = '',
    onChange,
    length = 6,
    disabled = false,
    error = false,
}) => {
    const inputRefs = useRef([]);

    const codeArray = useMemo(() => {
        const normalizedValue = (value || '').replace(/\D/g, '').slice(0, length);
        return Array.from({ length }, (_, index) => normalizedValue[index] || '');
    }, [length, value]);

    const focusInput = (index) => {
        if (index < 0 || index >= length) {
            return;
        }
        inputRefs.current[index]?.focus();
    };

    const updateCode = (nextArray) => {
        if (onChange) {
            onChange(nextArray.join(''));
        }
    };

    const handleDigitChange = (index, rawValue) => {
        if (disabled) {
            return;
        }

        const digitsOnly = rawValue.replace(/\D/g, '');
        const nextArray = [...codeArray];

        if (!digitsOnly) {
            nextArray[index] = '';
            updateCode(nextArray);
            return;
        }

        if (digitsOnly.length > 1) {
            const spreadDigits = digitsOnly.slice(0, length - index).split('');
            spreadDigits.forEach((digit, offset) => {
                nextArray[index + offset] = digit;
            });
            updateCode(nextArray);
            focusInput(Math.min(index + spreadDigits.length, length - 1));
            return;
        }

        nextArray[index] = digitsOnly;
        updateCode(nextArray);
        focusInput(index + 1);
    };

    const handleKeyDown = (index, event) => {
        if (disabled) {
            return;
        }

        if (event.key === 'Backspace' && !codeArray[index]) {
            focusInput(index - 1);
        }

        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            focusInput(index - 1);
        }

        if (event.key === 'ArrowRight') {
            event.preventDefault();
            focusInput(index + 1);
        }
    };

    const handlePaste = (index, event) => {
        if (disabled) {
            return;
        }

        event.preventDefault();
        const pastedValue = event.clipboardData.getData('text').replace(/\D/g, '');

        if (!pastedValue) {
            return;
        }

        const nextArray = [...codeArray];
        const spreadDigits = pastedValue.slice(0, length - index).split('');

        spreadDigits.forEach((digit, offset) => {
            nextArray[index + offset] = digit;
        });

        updateCode(nextArray);
        focusInput(Math.min(index + spreadDigits.length, length - 1));
    };

    return (
        <Box sx={{ display: 'flex', gap: { xs: '0.4rem', md: '0.5rem' }, width: '100%' }}>
            {codeArray.map((digit, index) => (
                <TextField
                    key={index}
                    inputRef={(element) => {
                        inputRefs.current[index] = element;
                    }}
                    value={digit}
                    disabled={disabled}
                    error={error}
                    onChange={(event) => handleDigitChange(index, event.target.value)}
                    onKeyDown={(event) => handleKeyDown(index, event)}
                    onPaste={(event) => handlePaste(index, event)}
                    inputProps={{
                        maxLength: 1,
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                        style: {
                            textAlign: 'center',
                            fontWeight: 700,
                            fontSize: '1rem',
                            padding: '0.5rem 0',
                        },
                    }}
                    sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '0.25rem',
                            backgroundColor: '#FFFEFC',
                        },
                    }}
                    size='small'
                />
            ))}
        </Box>
    );
};

export default VerificationCodeInput;
